import type { HTMLAttributes, ReactNode } from "react";
import { Icon, type IconName } from "./icons";

// Card + StatCard — codified from 09-kartlar.html. Card is the calm surface container
// (soft border, 16px radius, measured shadow); StatCard is the metric tile (icon chip
// + optional delta + value + label), including the inverted (Privé-blue) hero variant.

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: "sm" | "md" | "lg";
  inverted?: boolean;
}

export function Card({ padding = "md", inverted = false, className, ...props }: CardProps) {
  const classes = [
    "k-card",
    padding === "sm" ? "k-card--pad-sm" : padding === "lg" ? "k-card--pad-lg" : "",
    inverted ? "k-card--inverted" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return <div className={classes} {...props} />;
}

export type StatTone = "blue" | "green" | "amber" | "onblue";

export interface StatCardProps {
  icon: IconName;
  iconTone?: StatTone;
  value: ReactNode;
  label: ReactNode;
  /** A trend delta chip, e.g. { text: "%4,2", direction: "up" }. */
  delta?: { text: ReactNode; direction?: "up" };
  /** A plain text tag in the delta slot (e.g. "Mükemmel"). */
  note?: ReactNode;
  inverted?: boolean;
  /** An optional calm accent hairline — the four-eyes KPI uses "amber" (04). */
  accent?: "amber";
}

export function StatCard({
  icon,
  iconTone = "blue",
  value,
  label,
  delta,
  note,
  inverted = false,
  accent,
}: StatCardProps) {
  return (
    <Card
      padding="sm"
      inverted={inverted}
      className={accent ? `k-card--accent-${accent}` : undefined}
    >
      <div className="k-stat__top">
        <span className={`k-stat__icon k-stat__icon--${iconTone}`}>
          <Icon name={icon} size={18} strokeWidth={1.8} />
        </span>
        {delta ? (
          <span className="k-stat__delta">
            <Icon name="trend-up" size={12} strokeWidth={2.5} />
            {delta.text}
          </span>
        ) : note ? (
          <span className="k-stat__delta" style={inverted ? { color: "#7fe3b2" } : undefined}>
            {note}
          </span>
        ) : null}
      </div>
      <div className="k-stat__value">{value}</div>
      <div className="k-stat__label">{label}</div>
    </Card>
  );
}
